// utils/rackColumnConfigUrl.ts
import {
  RackColumnConfiguration,
  ShelfSpacingPreset,
} from '~/types/rackConfigurationTypes'

/**
 * Mapping between rack template IDs and compact URL codes
 * Using short codes to keep URLs manageable
 */
const TEMPLATE_TO_CODE: Record<string, string> = {
  OPEN_SHELVES_ONLY: 'OS',
  SHELVES_WITH_FULL_DOOR: 'FD',
  HALF_OPEN_HALF_CLOSED: 'HC',
  OPEN_AND_BOTTOM_CLOSED: 'OB',
  OPEN_AND_SMALL_BOTTOM_CLOSED: 'OSB',
  OPEN_SHELVES_AND_DRAWERS: 'ODS',
  OPEN_SHELVES_AND_BOTTOM_DRAWERS: 'OBD',
}

const CODE_TO_TEMPLATE: Record<string, string> = Object.fromEntries(
  Object.entries(TEMPLATE_TO_CODE).map(([template, code]) => [code, template])
)

const SPACING_TO_CODE: Record<ShelfSpacingPreset, string> = {
  small: 'S',
  medium: 'M',
  large: 'L',
}

const CODE_TO_SPACING: Record<string, ShelfSpacingPreset> = Object.fromEntries(
  Object.entries(SPACING_TO_CODE).map(([preset, code]) => [code, preset])
) as Record<string, ShelfSpacingPreset>

/**
 * Encode rack column configurations to URL-friendly string
 * Format: "OS,FD,HC" (comma-separated template codes)
 *
 * @example
 * encodeRackColumnConfigs([
 *   { templateId: 'OPEN_SHELVES_ONLY', ... },
 *   { templateId: 'SHELVES_WITH_FULL_DOOR', ... }
 * ])
 * // Returns: "OS,FD"
 */
export function encodeRackColumnConfigs(
  configs: RackColumnConfiguration[]
): string {
  const templates = configs
    .map((config) => {
      if (config.templateId && TEMPLATE_TO_CODE[config.templateId]) {
        return TEMPLATE_TO_CODE[config.templateId]
      }
      return 'OS'
    })
    .join(',')

  const spacing = configs[0]?.shelfSpacingPreset ?? 'large'
  if (spacing === 'large') return templates

  return `${templates};${SPACING_TO_CODE[spacing]}`
}

/**
 * Decode URL string to rack column configurations
 * Returns template IDs and optional shelf spacing preset
 *
 * Format: "OS,FD,HC" or "OS,FD,HC;M" (with shelf spacing suffix)
 *
 * @example
 * decodeRackColumnConfigs("OS,FD;M")
 * // Returns: { templateIds: ["OPEN_SHELVES_ONLY", "SHELVES_WITH_FULL_DOOR"], shelfSpacingPreset: "medium" }
 */
export function decodeRackColumnConfigs(encoded: string): {
  templateIds: string[]
  shelfSpacingPreset: ShelfSpacingPreset
} {
  if (!encoded || typeof encoded !== 'string') {
    return { templateIds: [], shelfSpacingPreset: 'large' }
  }

  const [templatesPart, spacingCode] = encoded.split(';')
  const shelfSpacingPreset: ShelfSpacingPreset =
    (spacingCode && CODE_TO_SPACING[spacingCode.trim()]) || 'large'

  const templateIds: string[] = []

  for (const code of templatesPart.split(',')) {
    const trimmedCode = code.trim()
    if (!trimmedCode) continue

    const templateId = CODE_TO_TEMPLATE[trimmedCode]
    if (!templateId) {
      console.warn(`Unknown rack configuration code: ${trimmedCode}`)
      templateIds.push('OPEN_SHELVES_ONLY')
    } else {
      templateIds.push(templateId)
    }
  }

  return { templateIds, shelfSpacingPreset }
}

